import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Save, X, Trash2, Edit as EditIcon, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyPost = { title: '', excerpt: '', content: '', keywords: '', author_name: 'Eat From Local', published: true };

export const BlogAdminTab = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id?, ...post }
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/blog`, { withCredentials: true });
      setPosts(data);
    } catch (e) {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...emptyPost });
  const startEdit = (p) => setEditing({
    ...p,
    keywords: Array.isArray(p.keywords) ? p.keywords.join(', ') : ''
  });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.title.trim() || !editing.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    const payload = {
      title: editing.title.trim(),
      excerpt: editing.excerpt || '',
      content: editing.content,
      author_name: editing.author_name || 'Eat From Local',
      published: !!editing.published,
      keywords: (editing.keywords || '').split(',').map((k) => k.trim()).filter(Boolean),
      cover_image: editing.cover_image || null
    };
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/blog/${editing.id}`, payload, { withCredentials: true });
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/admin/blog`, payload, { withCredentials: true });
        toast.success('Post created');
      }
      setEditing(null);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/blog/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Post deleted');
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const input = (name, placeholder, opts = {}) => (
    <input
      type="text"
      value={editing[name] || ''}
      onChange={(e) => setEditing({ ...editing, [name]: e.target.value })}
      placeholder={placeholder}
      className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
      style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
      data-testid={`blog-input-${name}`}
      {...opts}
    />
  );

  if (editing) {
    return (
      <div className="rounded-md border border-[#E5E0D8] p-5 md:p-8" style={{ backgroundColor: '#FFFFFF' }} data-testid="blog-editor">
        <div className="flex items-center justify-between mb-6">
          <h3 style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, fontSize: '24px', color: '#1A1A1A' }}>
            {editing.id ? 'Edit blog post' : 'New blog post'}
          </h3>
          <Button variant="ghost" size="sm" onClick={cancel} style={{ fontFamily: 'Manrope' }} data-testid="blog-editor-cancel">
            <X size={16} className="mr-1" /> Cancel
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <label style={{ fontFamily: 'Manrope', fontSize: '13px', fontWeight: 600, color: '#5C5C5C' }} className="block mb-2">Title *</label>
            {input('title', 'e.g. Food safety essentials every home cook should know')}
          </div>
          <div>
            <label style={{ fontFamily: 'Manrope', fontSize: '13px', fontWeight: 600, color: '#5C5C5C' }} className="block mb-2">Excerpt</label>
            <textarea
              value={editing.excerpt || ''}
              onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
              rows={2}
              placeholder="1-2 sentence summary shown on the blog index and in search results"
              className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
              data-testid="blog-input-excerpt"
            />
          </div>
          <div>
            <label style={{ fontFamily: 'Manrope', fontSize: '13px', fontWeight: 600, color: '#5C5C5C' }} className="block mb-2">
              Content * <span style={{ fontWeight: 400, color: '#8A8073' }}>(supports <code>## Heading</code>, <code>- bullets</code>, <code>**bold**</code>)</span>
            </label>
            <textarea
              value={editing.content || ''}
              onChange={(e) => setEditing({ ...editing, content: e.target.value })}
              rows={16}
              placeholder="Write your post…"
              className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0', lineHeight: 1.6 }}
              data-testid="blog-input-content"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label style={{ fontFamily: 'Manrope', fontSize: '13px', fontWeight: 600, color: '#5C5C5C' }} className="block mb-2">Author name</label>
              {input('author_name', 'Eat From Local')}
            </div>
            <div>
              <label style={{ fontFamily: 'Manrope', fontSize: '13px', fontWeight: 600, color: '#5C5C5C' }} className="block mb-2">SEO keywords (comma-separated)</label>
              {input('keywords', 'home cook food near me, sell food from home')}
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <input
              id="blog-published"
              type="checkbox"
              checked={!!editing.published}
              onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
              data-testid="blog-input-published"
            />
            <label htmlFor="blog-published" style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#3D3D3D' }}>
              Published (visible to visitors)
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <Button variant="outline" onClick={cancel} style={{ fontFamily: 'Manrope' }}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-md"
            style={{ backgroundColor: '#2E5C41', fontFamily: 'Manrope', fontWeight: 600 }}
            data-testid="blog-editor-save"
          >
            <Save size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save post'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-admin-list">
      <div className="flex items-center justify-between mb-4">
        <p style={{ fontFamily: 'Manrope', color: '#5C5C5C', fontSize: '14px' }}>
          {loading ? 'Loading…' : `${posts.length} post${posts.length === 1 ? '' : 's'} total`}
        </p>
        <Button
          onClick={startNew}
          className="rounded-md"
          style={{ backgroundColor: '#D95A41', fontFamily: 'Manrope', fontWeight: 600 }}
          data-testid="blog-new-btn"
        >
          <Plus size={16} className="mr-1" /> New post
        </Button>
      </div>

      {posts.length === 0 && !loading ? (
        <p className="text-center py-12" style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>No posts yet.</p>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div
              key={p.id}
              className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 md:p-5 rounded-md border border-[#E5E0D8]"
              style={{ backgroundColor: '#FFFFFF' }}
              data-testid={`blog-row-${p.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="uppercase px-2 py-0.5 rounded"
                    style={{
                      fontFamily: 'Manrope', fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em',
                      backgroundColor: p.published ? '#E5EFE4' : '#F2E4C9',
                      color: p.published ? '#2E5C41' : '#7A5A0F'
                    }}
                  >
                    {p.published ? 'Published' : 'Draft'}
                  </span>
                  <span style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#8A8073' }}>/blog/{p.slug}</span>
                </div>
                <div style={{ fontFamily: 'Cormorant Garamond', fontSize: '20px', fontWeight: 600, color: '#1A1A1A' }} className="truncate">
                  {p.title}
                </div>
                {p.excerpt && (
                  <p style={{ fontFamily: 'Manrope', fontSize: '13px', color: '#5C5C5C' }} className="line-clamp-1 mt-1">
                    {p.excerpt}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`/blog/${p.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center px-3 py-1.5 rounded-md border border-[#E5E0D8] hover:border-[#D95A41]"
                  style={{ fontFamily: 'Manrope', fontSize: '13px', color: '#5C5C5C' }}
                >
                  <ExternalLink size={13} className="mr-1" strokeWidth={1.5} /> View
                </a>
                <Button
                  variant="outline" size="sm" className="rounded-md"
                  onClick={() => startEdit(p)}
                  style={{ borderColor: '#D95A41', color: '#D95A41', fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px' }}
                  data-testid={`blog-edit-${p.id}`}
                >
                  <EditIcon size={13} className="mr-1" /> Edit
                </Button>
                <Button
                  variant="outline" size="sm" className="rounded-md"
                  onClick={() => setDeleteTarget(p)}
                  style={{ borderColor: '#991B1B', color: '#991B1B', fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px' }}
                  data-testid={`blog-delete-${p.id}`}
                >
                  <Trash2 size={13} className="mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="blog-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
              Delete "{deleteTarget?.title}"?
            </AlertDialogTitle>
            <AlertDialogDescription style={{ fontFamily: 'Manrope' }}>
              This permanently removes the blog post. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ fontFamily: 'Manrope' }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              style={{ backgroundColor: '#991B1B', fontFamily: 'Manrope', fontWeight: 600 }}
              data-testid="blog-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
